import { ajvResolver } from '@hookform/resolvers/ajv';
import { FieldValidationState } from '@payaca/ambrosia';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { JSONSchemaType } from 'ajv';
import { diff as deepDiff } from 'deep-object-diff';
import { omit } from 'lodash-es';
import { useEffect } from 'react';
import { Control, FieldError, get, Resolver, useWatch } from 'react-hook-form';

import { VALID_EMAIL_REGEX } from '@payaca/constants';

const nonJsonSchemaKeys = [
  'combineProperties',
  'fixedInputs',
  'formClass',
  'json_schema_extra',
  'order',
  'recognisedInputId',
  'subtitle',
  'title',
  'tile',
  'discriminator',
];

const NORTHERN_IRELAND_POSTCODE = /^\s*BT/;

// Schema Preparation
// ------------------

export const meterNumberType = (postcode: string) => {
  if (NORTHERN_IRELAND_POSTCODE.test(postcode || '')) {
    return 'MPRN' as const;
  }
  return 'MPAN' as const;
};

export const prepareFormSchema = (formSchema: any): any => {
  formSchema = structuredClone(formSchema); // Avoid mutating prop
  if (!formSchema.combineProperties) return formSchema;
  Object.values(formSchema.combineProperties).forEach((combine: any) => {
    const [combineInto, combineFrom] = combine.combines;
    const combineFromProperties = formSchema.properties[combineFrom].properties;
    Object.values(combineFromProperties).forEach((property: any) => {
      // We still have to submit these fields in the namespace they were provided in
      property.json_schema_extra.namespaceOverride = combineFrom;
    });
    formSchema.properties[combineInto].properties = {
      ...formSchema.properties[combineInto].properties,
      ...formSchema.properties[combineFrom].properties,
    };
    delete formSchema.properties[combineFrom];
  });

  return formSchema;
};

export type RemovedFields = { [k: string]: true | RemovedFields };

export const prepareJSONSchema = (
  formSchema: any,
  removedFields: RemovedFields = {}
): JSONSchemaType<any> => {
  formSchema = structuredClone(formSchema); // Avoid mutating prop
  let schema = removeFormSchemaFromJSONSchema(formSchema);
  schema = removeSpecialCasesFromJSONSchema(schema, removedFields);
  return schema;
};

const removeFormSchemaFromJSONSchema = (schema: any): JSONSchemaType<any> => {
  const cleaned: any = omit(schema, nonJsonSchemaKeys);

  switch (cleaned.type) {
    case 'object':
      cleaned.properties = Object.fromEntries(
        Object.entries<any>(cleaned.properties ?? {}).map(([key, value]) => {
          const cleanedValue = removeFormSchemaFromJSONSchema(value);
          return [key, cleanedValue];
        })
      );
      break;
    case 'array':
      cleaned.items = removeFormSchemaFromJSONSchema(cleaned.items);
      break;
    case 'string':
      if (schema.json_schema_extra?.required === true) {
        // Without this, empty strings are considered valid
        cleaned.minLength = 1;
      }
      if (schema.json_schema_extra?.pattern) {
        // Fixes their invalid regex bug!
        cleaned.pattern = schema.json_schema_extra.pattern.value.replace(
          '\\#',
          '#'
        );
      }
  }

  if (cleaned.allOf) {
    cleaned.allOf = cleaned.allOf.map(removeFormSchemaFromJSONSchema);
  }
  if (cleaned.anyOf) {
    cleaned.anyOf = cleaned.anyOf.map(removeFormSchemaFromJSONSchema);
  }
  if (cleaned.oneOf) {
    cleaned.oneOf = cleaned.oneOf.map(removeFormSchemaFromJSONSchema);
  }
  if (cleaned.not) {
    cleaned.not = cleaned.not.map(removeFormSchemaFromJSONSchema);
  }

  if (typeof cleaned.definitions === 'object') {
    cleaned.definitions = Object.fromEntries(
      Object.entries<any>(cleaned.definitions).map(([key, value]) => {
        const cleanedValue = removeFormSchemaFromJSONSchema(value);
        return [key, cleanedValue];
      })
    );
  }

  return cleaned;
};

const removeSpecialCasesFromJSONSchema = (
  schema: any,
  fields: RemovedFields
): JSONSchemaType<any> => {
  schema.properties = Object.fromEntries(
    Object.entries<any>(schema.properties)
      // Remove entire property
      .filter(([key]) => fields[key] !== true)
      // Recurse into sub-schema, potentially removing properties within it
      .map(([key, subSchema]) => {
        if (!fields[key] || fields[key] === true) return [key, subSchema];
        return [key, removeSpecialCasesFromJSONSchema(subSchema, fields[key])];
      })
  );
  schema.required = schema.required.filter(
    (key: string) => fields[key] !== true
  );

  return schema;
};

// Validation Utilities
// --------------------

export const ajvSchemaOptions = {
  formats: {
    email: VALID_EMAIL_REGEX,
  },
};

export function generateJsonSchemaResolver(
  jsonSchema: JSONSchemaType<unknown>
): Resolver {
  return ajvResolver(jsonSchema, ajvSchemaOptions);
}

const validationMessages = (fieldError: FieldError) => {
  switch (fieldError.type) {
    // These are Zod errors where it has a nice user focussed output we can use
    case 'too_big':
    case 'not_multiple_of':
    case 'errorMessage':
      return [fieldError.message || 'Unknown error'];
    case 'invalid_union_discriminator':
      return ['Invalid format'];
    case 'too_small':
      return ['This field is required'];
    case 'custom':
      return [fieldError.message || 'Invalid input'];
    // These all Ajv errors where its not user focussed so we need to overwrite
    case 'required':
    case 'minLength':
    case 'type':
    case 'invalid_type':
      return ['This field is required'];
    case 'format':
      return ['Invalid format'];
    case 'enum':
      return ['Choose an option'];
    default:
      return [`Unknown error: ${fieldError.type} - ${fieldError.message}`];
  }
};

export const fieldValidationState = (
  fieldError: FieldError
): FieldValidationState => {
  return {
    isValid: false,
    validationMessages: validationMessages(fieldError),
  };
};

const fieldValidationResult = (
  fieldError: FieldError
): FieldValidationResult => {
  return {
    isValid: false,
    errors: validationMessages(fieldError),
  };
};

export function getAddressValidationState(errors: any, name: string) {
  const addressLines = ['line1', 'line2', 'city', 'postcode', 'country'];

  const addressValidationState: {
    [fieldname: string]: FieldValidationResult;
  } = {};
  const addressErrors = get(errors, name);

  if (addressErrors) {
    for (const addressLine of addressLines) {
      const error = addressErrors[addressLine];
      if (!error) continue;
      addressValidationState[addressLine] = fieldValidationResult(error);
    }
  }
  return addressValidationState;
}

// Form Data Utilities
// -------------------

function isUndefinedOrEmptyObject(value: unknown): boolean {
  return (
    value === undefined ||
    (typeof value === 'object' &&
      value !== null &&
      Object.keys(value).length === 0 &&
      !Array.isArray(value))
  );
}

/**
 *  WARNING: mutates the input object.
 *  Recursively removes key which are undefined or an empty object from
 *  the input object. Used for generating diffs to send to the server.
 **/
function removeUndefinedKeys(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      removeUndefinedKeys(item);
    }
  }

  for (const [key, val] of Object.entries(value)) {
    removeUndefinedKeys(val);
    if (isUndefinedOrEmptyObject(val)) {
      delete (value as Record<string, unknown>)[key];
    }
  }
}

export const usePersistFormChanges = ({
  control,
  persistedFormValues,
  persistChanges,
}: {
  control: Control;
  persistedFormValues: Record<string, unknown> | undefined;
  persistChanges: (changes: Record<string, unknown>) => Promise<void>;
}) => {
  const formValues = useWatch({
    control,
  });

  let changes: Record<string, unknown> = {};

  if (persistedFormValues) {
    const currentState = structuredClone(formValues);
    const serverState = structuredClone(persistedFormValues);
    removeUndefinedKeys(currentState);
    removeUndefinedKeys(serverState);
    changes = deepDiff(serverState, currentState, true) as Record<
      string,
      unknown
    >;
    removeUndefinedKeys(changes);
  }

  const hasChanges = !isUndefinedOrEmptyObject(changes);

  useEffect(() => {
    const debounced = setTimeout(() => {
      if (!hasChanges) return;

      void persistChanges(changes);
    }, 1000);

    return () => clearTimeout(debounced);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useEffect depends on `changes`, which changes every render
  }, [formValues, hasChanges, changes, persistChanges, persistedFormValues]);

  return hasChanges;
};

export const isMultipleFileSelection = (fieldSchema: any) => {
  return (
    (fieldSchema?.maxItems && fieldSchema.maxItems > 1) ||
    (!fieldSchema.maxItems &&
      fieldSchema.json_schema_extra?.type !== 'SINGLE ATTACHMENT INPUT 01')
  );
};
